'use client';

import classNames from 'classnames';
import Fuse from 'fuse.js';
import { useId, useMemo, useState } from 'react';

const DEFAULT_VISIBLE_COUNT = 8;

const FUSE_OPTIONS = {
  keys: ['searchable'],
  threshold: 0.35,
  ignoreLocation: true,
  minMatchCharLength: 2,
};

const SearchIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle cx="7" cy="7" r="5.25" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="m11 11 3 3"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

export const ContributorsIndexClient = ({
  items,
  categories,
  totalCount,
  header,
}) => {
  const [query, setQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showAll, setShowAll] = useState(false);
  const inputId = useId();

  const trimmedQuery = query.trim();
  const isSearching = trimmedQuery.length > 0;
  const hasFilter = selectedCategory.length > 0;
  const isFiltering = isSearching || hasFilter;

  const filteredByCategory = useMemo(
    () =>
      hasFilter
        ? items.filter((item) => item.categories.includes(selectedCategory))
        : items,
    [items, selectedCategory, hasFilter],
  );

  const fuse = useMemo(
    () => new Fuse(filteredByCategory, FUSE_OPTIONS),
    [filteredByCategory],
  );

  const matchedItems = useMemo(() => {
    if (!isSearching) return filteredByCategory;
    return fuse.search(trimmedQuery).map((result) => result.item);
  }, [fuse, filteredByCategory, isSearching, trimmedQuery]);

  const visibleItems =
    isFiltering || showAll
      ? matchedItems
      : matchedItems.slice(0, DEFAULT_VISIBLE_COUNT);

  const remainingCount = matchedItems.length - DEFAULT_VISIBLE_COUNT;
  const showSeeMore = !isFiltering && !showAll && remainingCount > 0;

  const handleQueryChange = (event) => {
    setQuery(event.target.value);
    setShowAll(false);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setShowAll(false);
  };

  const hasCategories = Array.isArray(categories) && categories.length > 0;
  const hasResults = visibleItems.length > 0;

  return (
    <div className="contributors-index__client">
      <div className="contributors-index__toolbar">
        <div className="contributors-index__toolbar__filter">
          <span className="contributors-index__toolbar__filter__label">
            Filtrer par expertise
          </span>

          {hasCategories ? (
            <div
              className="contributors-index__toolbar__filter__pills"
              role="radiogroup"
              aria-label="Filtrer par expertise"
            >
              <button
                type="button"
                role="radio"
                aria-checked={!hasFilter}
                className={classNames('contributors-index__pill', {
                  'contributors-index__pill--active': !hasFilter,
                })}
                onClick={() => handleCategoryChange('')}
              >
                <span>Tout voir</span>
                <span className="contributors-index__pill__count">
                  ({totalCount})
                </span>
              </button>

              {categories.map((category) => {
                const active = selectedCategory === category;
                return (
                  <button
                    key={category}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    className={classNames('contributors-index__pill', {
                      'contributors-index__pill--active': active,
                    })}
                    onClick={() => handleCategoryChange(category)}
                  >
                    {category}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>

        <label className="contributors-index__search" htmlFor={inputId}>
          <input
            id={inputId}
            type="search"
            className="contributors-index__search__input"
            value={query}
            onChange={handleQueryChange}
            placeholder="Rechercher un expert…"
            autoComplete="off"
          />
          <span className="contributors-index__search__icon" aria-hidden="true">
            <SearchIcon />
          </span>
        </label>
      </div>

      {header}

      {hasResults ? (
        <div className="contributors-index__grid">
          {visibleItems.map((item) => (
            <div key={item.id} className="contributors-index__cell">
              {item.node}
            </div>
          ))}
        </div>
      ) : (
        <p className="contributors-index__empty">
          Aucun expert ne correspond à votre recherche.
        </p>
      )}

      {showSeeMore ? (
        <div className="contributors-index__more">
          <button
            type="button"
            className="contributors-index__more__button"
            onClick={() => setShowAll(true)}
          >
            Voir tous les experts
          </button>
        </div>
      ) : null}
    </div>
  );
};
